// @flow
import type {AbstractComponent, Node} from 'react'
import React, {Component} from 'react'
import {createFragmentContainer, graphql} from 'react-relay'
import type {$RelayProps} from 'react-relay'
import Select from 'react-select'
import classNames from 'classnames'

import FormGroup from 'components/ui/FormGroup'
import type {Option} from 'components/proposals/types'
import sortByProp from 'utils/MiscUtils/sortByProp'
import {liftEdges} from 'utils/GraphQL'
import PremiumCallout from 'components/ui/PremiumCallout'

import type {RoleSelect_proposal as Proposal} from './__generated__/RoleSelect_proposal.graphql'

const availableRoleEdges = liftEdges('availableRoles')
const sortByLabel = sortByProp('label')

const makeOption = ({currentlyUpdating, node: {databaseId, isLinked, localizedName}}): Option => (
  isLinked ? ({
    label: I18n.t('linked_roles.name_with_suffix', {name: localizedName}),
    type: 'UpdateLinkedRole',
    value: databaseId,
    isDisabled: currentlyUpdating,
  }) : ({
    label: localizedName,
    type: 'UpdateRole',
    value: databaseId,
    isDisabled: currentlyUpdating,
  })
)

function craftCommand(proposal: Proposal, type: ?string, roleDatabaseId: ?string) {
  if (type === 'CreateRole') {
    return {
      databaseId: '',
      type,
    }
  }

  if (type === 'UpdateRole' || type === 'UpdateLinkedRole') {
    return {
      databaseId: roleDatabaseId,
      type,
    }
  }

  if (type === 'CreateLinkedRole') {
    return {
      originalRoleDatabaseId: null,
      circleDatabaseId: proposal.circle?.databaseId || null,
      type,
    }
  }

  return null
}

type Props = {
  className: string,
  disabled: boolean,
  onChange: (command: string, typename: string) => any,
  proposal: Proposal,
}

class RoleSelect extends Component<Props> {
  static defaultProps: { className: string } = {
    className: '',
  }

  onChange: ((Option) => void) = ({value: databaseId, type}: Option) => {
    const command = craftCommand(this.props.proposal, type, databaseId)

    if (type && command)
      this.props.onChange(JSON.stringify(command), `${type}Change`)
  }

  get proposalDatabaseId(): string {
    return this.props.proposal.databaseId
  }

  get canAddLinkedRoles(): boolean {
    return this.props.proposal.viewerCanLinkRole
  }

  get calloutEnabled(): boolean {
    return this.props.proposal.organization.isOnV5 && this.props.proposal.organization.onRestrictedPlan
  }

  get options(): Option[] {
    const options = availableRoleEdges(this.props.proposal).map(makeOption)
    const sortedOptions = sortByLabel(options)

    const newRoleCircleOption = {
      // eslint-disable-next-line no-irregular-whitespace
      label: `— ${I18n.t('proposals.controls.new_role_circle')} —`,
      value: 'create-role',
      type: 'CreateRole',
    }

    const newLinkedRoleOption = {
      // eslint-disable-next-line no-irregular-whitespace
      label: `— ${I18n.t('proposals.controls.invite_into_circle')} —`,
      value: 'create-linked-role',
      type: 'CreateLinkedRole',
    }

    return this.canAddLinkedRoles
      ? [newRoleCircleOption, newLinkedRoleOption, ...sortedOptions]
      : [newRoleCircleOption, ...sortedOptions]
  }

  get className(): string {
    return classNames(this.props.className, 'refinery-select')
  }

  get target(): string {
    return `PartSelector-${this.proposalDatabaseId}-role-select`
  }

  render(): Node {
    return (
      <FormGroup
        className="role-select-form"
        label={I18n.t('proposals.controls.add_edit_role')}
        small
        target={this.target}
        topRightAnnotation={this.calloutEnabled && (
          <PremiumCallout
            callout="linked_roles"
            targetText={I18n.t('premium_callout.linked_roles.target_text')}
            enabled
            darkIcon
          />
        )}
      >
        <Select
          isDisabled={this.props.disabled}
          className={this.className}
          classNamePrefix="Select"
          inputId={this.target}
          noOptionsMessage={() => I18n.t('shared.no_results_found')}
          onChange={this.onChange}
          value={null}
          options={this.options}
          placeholder={I18n.t('proposals.rewrite.select_input_choose_role')}
        />
      </FormGroup>
    )
  }
}

export default (createFragmentContainer(RoleSelect, {
  proposal: graphql`
    fragment RoleSelect_proposal on Proposal {
      databaseId
      viewerCanLinkRole

      organization {
        onRestrictedPlan
        isOnV5
      }
        
      circle {
        databaseId
      }

      availableRoles {
        edges {
          currentlyUpdating

          node {
            databaseId
            localizedName
            isLinked
          }
        }
      }
    }
  `,
}): AbstractComponent<
  $RelayProps<React$ElementConfig<typeof RoleSelect>>,
  RoleSelect,
>)
